import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import {
    type SelectSheetSelectionConstantType,
    type SelectSheetMaxChoiceConstantType,
    type SelectSheetDividerConstantType,
    type SelectSheetDynamicStyleType,
    type SelectSheetSelection
} from "./type";
import type {
    Mutable
} from "../../types";

export const SELECT_SHEET_STANDALONE_GROUP_KEY = "__select_sheet_standalone__";

export const SELECT_SHEET_ROW_TITLE_VARIANT: keyof NCoreUIKit.Typography = "bodyMediumSize";

export const SELECT_SHEET_SELECTED_BACKGROUND: keyof NCoreUIKit.ContainerContentColors = "selected";

export const getSelectSheetMaxChoice = ({
    maxChoice
}: SelectSheetMaxChoiceConstantType): number | undefined => {
    if(maxChoice === undefined || maxChoice < 0) {
        return undefined;
    }

    return maxChoice;
};

export const getSelectSheetSelection = ({
    selectedItems,
    maxChoice,
    minChoice
}: SelectSheetSelectionConstantType): SelectSheetSelection => {
    const currentMaxChoice = getSelectSheetMaxChoice({
        maxChoice
    });

    const selectedKeys = new Set(selectedItems.map((item) => item.__key));

    return {
        isMaxChoiceReached: currentMaxChoice !== undefined && selectedItems.length >= currentMaxChoice,
        isMinChoiceMet: !minChoice || selectedItems.length >= minChoice,
        selectedCount: selectedItems.length,
        maxChoice: currentMaxChoice,
        selectedKeys
    };
};

export const getSelectSheetIsDividerVisible = ({
    highlightedKeys,
    previousKey,
    nextKey
}: SelectSheetDividerConstantType): boolean => {
    if(previousKey === undefined) {
        return false;
    }

    return !highlightedKeys.has(previousKey) && !highlightedKeys.has(nextKey);
};

const stylesheet = StyleSheet.create({
    moreLoading: {
        justifyContent: "center",
        flexDirection: "row",
        alignItems: "center"
    },
    checkBoxSlot: {
        justifyContent: "center",
        alignItems: "center",
        flexShrink: 0
    },
    loading: {
        justifyContent: "center",
        alignItems: "center"
    },
    controls: {
        flexDirection: "column"
    },
    list: {
        flexDirection: "column"
    },
    footerActions: {
        flexDirection: "row",
        alignItems: "center",
        marginStart: "auto",
        flexShrink: 0
    },
    footerStatus: {
        flexDirection: "row",
        alignItems: "center",
        flexShrink: 1
    },
    rowLeading: {
        flexDirection: "row",
        alignItems: "center"
    },
    tools: {
        flexDirection: "row",
        alignItems: "center",
        flexWrap: "wrap"
    },
    footer: {
        flexDirection: "row",
        alignItems: "center"
    },
    counter: {
        marginStart: "auto"
    },
    footerText: {
        flexShrink: 1
    }
});

export const useStyles = ({
    controlHeights,
    iconSizes,
    borders,
    colors,
    spaces
}: SelectSheetDynamicStyleType) => {
    const styles = {
        divider: {
            backgroundColor: colors.content.border.subtle,
            marginHorizontal: spaces.spacingLg,
            height: borders.separator
        } as Mutable<ViewStyle>,
        controls: {
            paddingHorizontal: spaces.spacingLg,
            paddingVertical: spaces.spacingSm,
            gap: spaces.spacingSm
        } as Mutable<ViewStyle>,
        hiddenDivider: {
            marginHorizontal: spaces.spacingLg,
            height: borders.separator
        } as Mutable<ViewStyle>,
        loading: {
            paddingVertical: spaces.spacingXl,
            minHeight: controlHeights.large
        } as Mutable<ViewStyle>,
        emptyState: {
            paddingBottom: spaces.spacingXl,
            paddingStart: spaces.spacingLg,
            paddingEnd: spaces.spacingLg,
            paddingTop: spaces.spacingXl
        } as Mutable<ViewStyle>,
        moreLoading: {
            minHeight: controlHeights.large,
            gap: spaces.spacingSm
        } as Mutable<ViewStyle>,
        row: {
            paddingStart: spaces.spacingLg,
            paddingEnd: spaces.spacingLg
        } as Mutable<ViewStyle>,
        groupHeading: {
            marginStart: spaces.spacingXs
        } as Mutable<ViewStyle>,
        checkBoxSlot: {
            height: iconSizes.small,
            width: iconSizes.small
        } as Mutable<ViewStyle>,
        footerActions: {
            gap: spaces.spacingSm
        } as Mutable<ViewStyle>,
        footerStatus: {
            gap: spaces.spacingXs
        } as Mutable<ViewStyle>,
        rowLeading: {
            gap: spaces.spacingMd
        } as Mutable<ViewStyle>,
        footer: {
            gap: spaces.spacingSm
        } as Mutable<ViewStyle>,
        tools: {
            gap: spaces.spacingXs
        } as Mutable<ViewStyle>
    };

    return styles;
};
export default stylesheet;
